import React, { useState, useMemo, useCallback } from 'react'
import { useField } from 'formik'
import {
  format,
  getDay,
  sub,
  set,
  add,
  differenceInCalendarDays,
  isSameMonth,
  isToday,
  getWeeksInMonth,
  startOfDay,
} from 'date-fns'

import { XStack, YStack } from 'tamagui'

import { Text } from '../Text'
import { Icon } from '../Icon'

const SELECTED_COLOR = '$primaryColor'
const SELECTED_RANGE_BG = '$backgroundAccentMuted'
const TODAY_DOT_COLOR = '$primaryColor'
const CELL_SIZE = 36

export const FormCalendarDatePicker = ({
  onChangeStartDate,
  onChangeEndDate,
  startAtFieldName = 'startAt',
  endAtFieldName = 'endAt',
  selectingDate,
}: {
  onChangeStartDate?: (newDate: Date) => void
  onChangeEndDate?: (newDate?: Date) => void
  startAtFieldName?: string
  endAtFieldName?: string
  selectingDate: 'start' | 'end'
}) => {
  const [startAtField, , startAtHelpers] = useField<Date>(startAtFieldName)
  const [endAtField, , endAtHelpers] = useField<Date | undefined>(
    endAtFieldName
  )

  const [displayedMonth, setDisplayedMonth] = useState<Date>(() => {
    const initialDate = new Date(startAtField.value.getTime())

    initialDate.setDate(1)

    return initialDate
  })

  const [hoveredDate, setHoveredDate] = useState<Date | null>(null)

  const monthLabel = useMemo(() => {
    return format(displayedMonth, 'MMMM yyyy')
  }, [displayedMonth])

  const setStartDate = useCallback(
    (newDate: Date) => {
      const newStartDate = set(startAtField.value, {
        year: newDate.getFullYear(),
        month: newDate.getMonth(),
        date: newDate.getDate(),
      })

      void startAtHelpers.setValue(newStartDate)

      if (newStartDate.getMonth() !== displayedMonth.getMonth()) {
        const newDisplayedMonth = set(newStartDate, { date: 1 })

        setDisplayedMonth(newDisplayedMonth)
      }

      if (onChangeStartDate) {
        onChangeStartDate(newStartDate)
      }
    },
    [startAtField.value, startAtHelpers, displayedMonth, onChangeStartDate]
  )

  const setEndDate = useCallback(
    (newDate?: Date) => {
      if (!newDate) {
        void endAtHelpers.setValue(newDate)

        if (onChangeEndDate) {
          onChangeEndDate(newDate)
        }
        return
      }

      const newEndDate = set(endAtField.value ?? startAtField.value, {
        year: newDate.getFullYear(),
        month: newDate.getMonth(),
        date: newDate.getDate(),
      })

      void endAtHelpers.setValue(newEndDate)

      if (onChangeEndDate) {
        onChangeEndDate(newEndDate)
      }
    },
    [endAtField.value, startAtField.value, endAtHelpers, onChangeEndDate]
  )

  return (
    <YStack
      gap='$2'
      borderWidth={1}
      borderColor='$borderColor'
      borderRadius='$2'
      padding='$3'
      alignSelf='flex-start'
    >
      <XStack justifyContent='space-between' alignItems='center'>
        <Text size='$5' fontWeight='$6'>
          {monthLabel}
        </Text>

        <XStack gap='$2'>
          <YStack
            alignItems='center'
            justifyContent='center'
            padding='$1'
            borderRadius={999}
            cursor='pointer'
            hoverStyle={{ backgroundColor: '$backgroundStrong' }}
            pressStyle={{ backgroundColor: '$backgroundStrong' }}
            onPress={() => {
              setDisplayedMonth(sub(displayedMonth, { months: 1 }))
            }}
          >
            <Icon name='arrow-left' size='$4' />
          </YStack>
          <YStack
            alignItems='center'
            justifyContent='center'
            padding='$1'
            borderRadius={999}
            cursor='pointer'
            hoverStyle={{ backgroundColor: '$backgroundStrong' }}
            pressStyle={{ backgroundColor: '$backgroundStrong' }}
            onPress={() => {
              setDisplayedMonth(add(displayedMonth, { months: 1 }))
            }}
          >
            <Icon name='arrow-right' size='$4' />
          </YStack>
        </XStack>
      </XStack>

      <Month
        startDate={startAtField.value}
        endDate={endAtField.value}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        displayedMonth={displayedMonth}
        selectingDate={selectingDate}
        hoveredDate={hoveredDate}
        onHoverDate={setHoveredDate}
      />
    </YStack>
  )
}

const Month = ({
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  displayedMonth,
  selectingDate,
  hoveredDate,
  onHoverDate,
}: {
  startDate: Date
  endDate?: Date
  setStartDate: (newDate: Date) => void
  setEndDate: (newDate?: Date) => void
  displayedMonth: Date
  selectingDate: 'start' | 'end'
  hoveredDate: Date | null
  onHoverDate: (date: Date | null) => void
}) => {
  const firstDayOfCalendar = useMemo(() => {
    const dayOfWeek = getDay(displayedMonth)

    return sub(displayedMonth, { days: dayOfWeek })
  }, [displayedMonth])

  const selectDate = useCallback(
    (newDate: Date) => {
      switch (selectingDate) {
        case 'start': {
          setStartDate(newDate)

          if (endDate && endDate < newDate) {
            setEndDate(newDate)
          }
          return
        }

        default: {
          setEndDate(newDate)

          if (startDate > newDate) {
            setStartDate(newDate)
          }
        }
      }
    },
    [selectingDate, setStartDate, setEndDate, startDate, endDate]
  )

  // When selecting end date, use hovered date as preview
  const previewEndDate =
    selectingDate === 'end' && hoveredDate
      ? hoveredDate >= startDate
        ? hoveredDate
        : endDate
      : endDate

  // When hovering before start date while selecting end, preview swapping start
  const previewStartDate =
    selectingDate === 'end' && hoveredDate && hoveredDate < startDate
      ? hoveredDate
      : startDate

  return (
    <YStack gap='$2'>
      <YStack onMouseLeave={() => onHoverDate(null)}>
        <XStack alignItems='flex-start'>
          {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, index) => {
            return (
              <YStack
                key={index}
                width={CELL_SIZE}
                height={CELL_SIZE}
                alignItems='center'
                justifyContent='center'
              >
                <Text size='$3' fontWeight='$6' color='$colorSubtle'>
                  {day}
                </Text>
              </YStack>
            )
          })}
        </XStack>

        {Array.from({ length: getWeeksInMonth(displayedMonth) }).map(
          (_week, weekIndex) => {
            return (
              <XStack key={weekIndex} alignItems='flex-start'>
                {Array.from({ length: 7 }).map((_day, dayIndex) => (
                  <Day
                    key={dayIndex}
                    displayedMonth={displayedMonth}
                    firstDayOfCalendar={firstDayOfCalendar}
                    weekIndex={weekIndex}
                    dayIndex={dayIndex}
                    startDate={previewStartDate}
                    endDate={previewEndDate}
                    onPress={selectDate}
                    onHover={selectingDate === 'end' ? onHoverDate : undefined}
                  />
                ))}
              </XStack>
            )
          }
        )}
      </YStack>
    </YStack>
  )
}

const Day = ({
  firstDayOfCalendar,
  displayedMonth,
  weekIndex,
  dayIndex,
  startDate,
  endDate,
  onPress,
  onHover,
}: {
  firstDayOfCalendar: Date
  displayedMonth: Date
  weekIndex: number
  dayIndex: number
  startDate: Date
  endDate?: Date
  onPress?: (newDate: Date) => void
  onHover?: (date: Date | null) => void
}) => {
  const workingDate = useMemo(() => {
    return add(firstDayOfCalendar, {
      days: weekIndex * 7 + dayIndex,
    })
  }, [firstDayOfCalendar, weekIndex, dayIndex])

  const handlePress = useCallback(() => {
    if (onPress) {
      onPress(workingDate)
    }
  }, [onPress, workingDate])

  const handleMouseEnter = useCallback(() => {
    if (onHover) {
      onHover(workingDate)
    }
  }, [onHover, workingDate])

  const dayNumber = workingDate.getDate()
  const todayFlag = isToday(workingDate)

  // Determine the visual variant for this day
  if (endDate) {
    const diffFromEnd = differenceInCalendarDays(workingDate, endDate)
    const diffFromStart = differenceInCalendarDays(workingDate, startDate)

    if (diffFromEnd === 0 && diffFromStart === 0) {
      return (
        <DayCell
          variant='selected'
          onPress={handlePress}
          onMouseEnter={handleMouseEnter}
          dayNumber={dayNumber}
          today={todayFlag}
        />
      )
    }

    if (diffFromEnd === 0) {
      return (
        <DayCell
          variant='end'
          onPress={handlePress}
          onMouseEnter={handleMouseEnter}
          dayNumber={dayNumber}
          today={todayFlag}
        />
      )
    }

    if (diffFromStart === 0) {
      return (
        <DayCell
          variant='start'
          onPress={handlePress}
          onMouseEnter={handleMouseEnter}
          dayNumber={dayNumber}
          today={todayFlag}
        />
      )
    }

    if (workingDate < endDate && workingDate > startDate) {
      return (
        <DayCell
          variant='between'
          onPress={handlePress}
          onMouseEnter={handleMouseEnter}
          dayNumber={dayNumber}
          today={todayFlag}
        />
      )
    }
  } else if (startDate) {
    if (differenceInCalendarDays(workingDate, startDate) === 0) {
      return (
        <DayCell
          variant='selected'
          onPress={handlePress}
          onMouseEnter={handleMouseEnter}
          dayNumber={dayNumber}
          today={todayFlag}
        />
      )
    }
  }

  // Past days - not pressable
  if (startOfDay(workingDate) < startOfDay(new Date())) {
    return (
      <DayCell
        variant='past'
        onMouseEnter={handleMouseEnter}
        dayNumber={dayNumber}
        today={todayFlag}
      />
    )
  }

  // Current month days
  if (isSameMonth(displayedMonth, workingDate)) {
    return (
      <DayCell
        variant='normal'
        onPress={handlePress}
        onMouseEnter={handleMouseEnter}
        dayNumber={dayNumber}
        today={todayFlag}
      />
    )
  }

  // Outside current month
  return (
    <DayCell
      variant='outsideMonth'
      onPress={handlePress}
      onMouseEnter={handleMouseEnter}
      dayNumber={dayNumber}
      today={todayFlag}
    />
  )
}

type DayVariant =
  | 'normal'
  | 'selected'
  | 'start'
  | 'end'
  | 'between'
  | 'outsideMonth'
  | 'past'

const DayCell = ({
  variant,
  onPress,
  onMouseEnter,
  dayNumber,
  today = false,
}: {
  variant: DayVariant
  onPress?: () => void
  onMouseEnter?: () => void
  dayNumber: number
  today?: boolean
}) => {
  const isSelected = variant === 'selected'
  const isStart = variant === 'start'
  const isEnd = variant === 'end'
  const isBetween = variant === 'between'
  const isOutsideMonth = variant === 'outsideMonth'
  const isPast = variant === 'past'

  const hasHighlight = isSelected || isStart || isEnd
  const isFaded = isOutsideMonth || isPast

  const backgroundColor = hasHighlight
    ? SELECTED_COLOR
    : isBetween
      ? SELECTED_RANGE_BG
      : 'transparent'

  const halfSize = CELL_SIZE / 2
  const borderTopLeftRadius = isSelected || isStart ? halfSize : 0
  const borderBottomLeftRadius = isSelected || isStart ? halfSize : 0
  const borderTopRightRadius = isSelected || isEnd ? halfSize : 0
  const borderBottomRightRadius = isSelected || isEnd ? halfSize : 0

  const textColor = hasHighlight ? 'white' : isFaded ? '$colorFaint' : '$color'
  const isDisabled = isPast && !hasHighlight
  const cursor = isDisabled ? 'not-allowed' : onPress ? 'pointer' : 'default'

  return (
    <YStack
      width={CELL_SIZE}
      height={CELL_SIZE}
      alignItems='center'
      justifyContent='center'
      backgroundColor={backgroundColor}
      borderTopLeftRadius={borderTopLeftRadius}
      borderBottomLeftRadius={borderBottomLeftRadius}
      borderTopRightRadius={borderTopRightRadius}
      borderBottomRightRadius={borderBottomRightRadius}
      cursor={cursor}
      pressStyle={onPress && !isDisabled ? { opacity: 0.7 } : undefined}
      onPress={isDisabled ? undefined : onPress}
      onMouseEnter={onMouseEnter}
    >
      <Text
        size='$4'
        color={textColor}
        userSelect='none'
        fontWeight={today ? '$7' : undefined}
      >
        {dayNumber}
      </Text>
      {today && (
        <YStack
          position='absolute'
          bottom={2}
          width={4}
          height={4}
          borderRadius={2}
          backgroundColor={hasHighlight ? 'white' : TODAY_DOT_COLOR}
        />
      )}
    </YStack>
  )
}
